import LoadingScreen from '@/components/LoadingScreen'
import { useState } from 'react'
import { useAppSelector } from '@/hooks/useRedux'
import { callService } from '@/services/api'
import type { User } from '@/types'

export default function CallsPage() {
  const { user } = useAppSelector((state) => state.auth)
  const [contacts] = useState<User[]>([])
  const [activeCall, setActiveCall] = useState<any | null>(null)
  const [incomingCall, setIncomingCall] = useState<any | null>(null)

  const handleInitiateCall = async (targetUserId: string, type: 'voice' | 'video') => {
    try {
      const response = await callService.initiate(targetUserId, type)
      setActiveCall(response.data)
    } catch (error) {
      console.error('Failed to initiate call:', error)
    }
  }

  const handleAcceptCall = async () => {
    if (!incomingCall) return
    try {
      await callService.accept(incomingCall.id)
      setActiveCall(incomingCall)
      setIncomingCall(null)
    } catch (error) {
      console.error('Failed to accept call:', error)
    }
  }

  const handleDeclineCall = async () => {
    if (!incomingCall) return
    try {
      await callService.decline(incomingCall.id)
      setIncomingCall(null)
    } catch (error) {
      console.error('Failed to decline call:', error)
    }
  }

  const handleEndCall = async () => {
    if (!activeCall) return
    try {
      await callService.end(activeCall.id)
      setActiveCall(null)
    } catch (error) {
      console.error('Failed to end call:', error)
    }
  }

  if (!user) {
    return <LoadingScreen />
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <h1 className="text-4xl font-bold text-white mb-8">Voice & Video Calls</h1>

      {/* Incoming Call Alert */}
      {incomingCall && (
        <div className="bg-purple-900 border-2 border-purple-500 rounded-lg p-6 mb-8">
          <p className="text-white mb-4">
            Incoming call from {incomingCall.caller_name}
          </p>
          <div className="flex gap-4">
            <button
              onClick={handleAcceptCall}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded"
            >
              Accept
            </button>
            <button
              onClick={handleDeclineCall}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded"
            >
              Decline
            </button>
          </div>
        </div>
      )}

      {/* Active Call */}
      {activeCall && (
        <div className="bg-gray-800 border-2 border-gray-700 rounded-lg p-6 mb-8">
          <p className="text-white mb-4">
            In call with {activeCall.target_name}
          </p>
          <button
            onClick={handleEndCall}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded"
          >
            End Call
          </button>
        </div>
      )}

      {/* Contacts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contacts.length === 0 ? (
          <p className="text-gray-400">No contacts available</p>
        ) : (
          contacts.map((contact) => (
            <div key={contact.id} className="bg-gray-800 rounded-lg p-4">
              <img
                src={contact.avatar_url || 'https://via.placeholder.com/64'}
                alt={contact.display_name}
                className="w-12 h-12 rounded-full mb-2"
              />
              <h3 className="text-white font-bold mb-2">{contact.display_name}</h3>
              <p className="text-gray-400 text-sm mb-4">@{contact.username}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleInitiateCall(contact.id, 'voice')}
                  disabled={!!activeCall}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white py-2 px-4 rounded text-sm"
                >
                  Voice
                </button>
                <button
                  onClick={() => handleInitiateCall(contact.id, 'video')}
                  disabled={!!activeCall}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white py-2 px-4 rounded text-sm"
                >
                  Video
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
